import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { PartnerUser, PartnerAuthContextType } from '../types/partner';
import { partnerAuthApi } from '../services/partnerApi';
import {
  getPartnerToken,
  setPartnerToken,
  removePartnerToken,
  getCurrentPartnerUser,
  hasPartnerRole as utilHasPartnerRole,
  parsePartnerToken
} from '../utils/partnerAuth';

const PartnerAuthContext = createContext<PartnerAuthContextType | undefined>(undefined);

interface PartnerAuthProviderProps {
  children: ReactNode;
}

export const PartnerAuthProvider: React.FC<PartnerAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<PartnerUser | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = getPartnerToken();
        console.log('Initializing partner auth, stored token:', storedToken ? 'exists' : 'none');

        if (storedToken) {
          setTokenState(storedToken);
          const currentUser = getCurrentPartnerUser();
          
          if (currentUser) {
            console.log('Found current partner user from token:', currentUser);

            // Load full profile from backend
            try {
              const profile = await partnerAuthApi.getProfile();
              console.log('Loaded partner profile from backend:', profile);
              
              const fullUser: PartnerUser = {
                ...currentUser,
                emailVerified: profile.emailVerified,
                mfaEnabled: profile.mfaEnabled,
                createdAt: profile.createdAt,
                lastLoginAt: profile.lastLoginAt
              };
              setUser(fullUser);
            } catch (profileError) {
              console.error('Error loading partner profile:', profileError);
              // Fall back to token data
              setUser(currentUser);
            }
          } else {
            console.log('Partner token is invalid or expired, clearing');
            // Token is invalid, clear it
            removePartnerToken();
            setTokenState(null);
            setUser(null);
          }
        } else {
          console.log('No partner token found');
        }
      } catch (error) {
        console.error('Error initializing partner auth:', error);
        removePartnerToken();
        setTokenState(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await partnerAuthApi.login({ email, password });

      setPartnerToken(response.token);
      setTokenState(response.token);

      // Create user object from login response
      const user: PartnerUser = {
        id: response.userId,
        email: response.email,
        displayName: response.displayName,
        role: response.role,
        partnerId: response.partnerId,
        emailVerified: response.emailVerified,
        mfaEnabled: response.mfaEnabled
      };

      setUser(user);
    } catch (error) {
      console.error('Partner login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName: string | undefined,
    inviteToken: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const requestData: any = {
        email,
        password,
        inviteToken
      };

      // Only include displayName if it has a value
      if (displayName) {
        requestData.displayName = displayName;
      }

      const response = await partnerAuthApi.register(requestData);

      // Registration successful, email auto-verified, user is now logged in
      if (response.token) {
        setPartnerToken(response.token);
        setTokenState(response.token);

        // Create user object from registration response
        const user: PartnerUser = {
          id: response.userId,
          email: response.email,
          displayName: response.displayName,
          role: response.role,
          partnerId: response.partnerId,
          emailVerified: response.emailVerified,
          mfaEnabled: response.mfaEnabled
        };

        setUser(user);
      }
    } catch (error) {
      console.error('Partner registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Call backend logout endpoint
      if (token) {
        await partnerAuthApi.logout();
      }
    } catch (error) {
      console.error('Partner logout error:', error);
    } finally {
      // Clear local state regardless of backend response
      removePartnerToken();
      setTokenState(null);
      setUser(null);
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const response = await partnerAuthApi.refreshToken();
      setPartnerToken(response.token);
      setTokenState(response.token);

      const userFromToken = getCurrentPartnerUser();
      if (userFromToken) {
        setUser(userFromToken);
      }
    } catch (error) {
      console.error('Partner token refresh error:', error);
      // If refresh fails, logout
      await logout();
      throw error;
    }
  };

  const hasRole = (role: string): boolean => {
    return utilHasPartnerRole(user, role);
  };

  const isAuthenticated = Boolean(token && user);

  const value: PartnerAuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    refreshToken,
    isAuthenticated,
    isLoading,
    hasRole,
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export const usePartnerAuth = (): PartnerAuthContextType => {
  const context = useContext(PartnerAuthContext);
  if (context === undefined) {
    throw new Error('usePartnerAuth must be used within a PartnerAuthProvider');
  }
  return context;
};

